import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';

// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

export type PreviewDevice =
  | 'auto'
  | 'iphone-15-pro-max'
  | 'iphone-15-pro'
  | 'iphone-15'
  | 'iphone-14-pro-max'
  | 'iphone-14-pro'
  | 'iphone-14'
  | 'iphone-se-3rd-gen'
  | 'ipad-pro-13'
  | 'ipad-pro-11'
  | 'ipad-air-5th-gen'
  | 'ipad-mini-6th-gen'
  | 'pixel-8-pro'
  | 'pixel-8'
  | 'galaxy-s24-ultra'
  | 'galaxy-s24'
  | 'galaxy-z-fold5'
  | 'galaxy-tab-s9-ultra'
  | 'surface-pro-9'
  | 'macbook-pro-16'
  | 'macbook-air-15'
  | 'windows-laptop-15'
  | 'desktop-4k'
  | 'desktop-1440p'
  | 'desktop-1080p';

const PREVIEW_DEVICE_KEY = 'preview-device';
const PREVIEW_DEVICE_EVENT = 'preview-device-changed';
const PREVIEW_LAYOUT_CONTEXT_EVENT = 'preview-layout-context-changed';
const PREVIEW_DEVICE_COLLECTION = 'uiPrefs';
const PREVIEW_DEVICE_DOC_ID = 'previewDevice';

const shouldDisableFirestore = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';

const getPreviewDeviceDocRef = async () => {
  const db = await getDb();
  if (!db) throw new Error('Firestore is not configured.');
  return doc(db, PREVIEW_DEVICE_COLLECTION, PREVIEW_DEVICE_DOC_ID);
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] previewDevice', detail);
  } catch {
    // ignore
  }
};

const isEmbeddedPreviewContext = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1') return true;
  } catch {
    // ignore
  }

  try {
    return typeof document !== 'undefined' && document.body.classList.contains('preview-embedded');
  } catch {
    return false;
  }
};

const isPreviewLayoutContext = (): boolean => {
  if (isEmbeddedPreviewContext()) return true;
  try {
    return typeof document !== 'undefined' && document.body.classList.contains('preview-layout');
  } catch {
    return false;
  }
};

const PREVIEW_DEVICE_LIST: PreviewDevice[] = [
  'auto',
  'iphone-15-pro-max',
  'iphone-15-pro',
  'iphone-15',
  'iphone-14-pro-max',
  'iphone-14-pro',
  'iphone-14',
  'iphone-se-3rd-gen',
  'ipad-pro-13',
  'ipad-pro-11',
  'ipad-air-5th-gen',
  'ipad-mini-6th-gen',
  'pixel-8-pro',
  'pixel-8',
  'galaxy-s24-ultra',
  'galaxy-s24',
  'galaxy-z-fold5',
  'galaxy-tab-s9-ultra',
  'surface-pro-9',
  'macbook-pro-16',
  'macbook-air-15',
  'windows-laptop-15',
  'desktop-4k',
  'desktop-1440p',
  'desktop-1080p',
];

const isPreviewDevice = (value: unknown): value is PreviewDevice => {
  return PREVIEW_DEVICE_LIST.includes(value as PreviewDevice);
};

export const getPreviewDeviceList = () => PREVIEW_DEVICE_LIST;

export const getStoredPreviewDevice = (): PreviewDevice => {
  if (typeof window === 'undefined') return 'auto';
  try {
    const stored = window.localStorage.getItem(PREVIEW_DEVICE_KEY);
    return isPreviewDevice(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
};

export const setStoredPreviewDevice = (next: PreviewDevice) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREVIEW_DEVICE_KEY, next);
    window.dispatchEvent(new Event(PREVIEW_DEVICE_EVENT));
  } catch {
    // ignore storage errors
  }

  (async () => {
    const db = await getDb();
    if (db && !shouldDisableFirestore()) {
      try {
        logFirestoreWrite({ action: 'savePreviewDevice', device: next });
        void setDoc(await getPreviewDeviceDocRef(), { device: next }, { merge: true });
      } catch {
        // ignore network errors
      }
    }
  })();
};

const fetchRemotePreviewDevice = async (): Promise<PreviewDevice | null> => {
  const db = await getDb();
  if (!db || shouldDisableFirestore()) return null;
  const ref = await getPreviewDeviceDocRef();
  try {
    const snapshot = await getDocFromServer(ref);
    if (snapshot.exists()) {
      const device = (snapshot.data() as { device?: string }).device;
      return isPreviewDevice(device) ? device : null;
    }
  } catch {
    try {
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        const device = (snapshot.data() as { device?: string }).device;
        return isPreviewDevice(device) ? device : null;
      }
    } catch {
      return null;
    }
  }
  return null;
};

const applyBodyPreviewDevice = (value: PreviewDevice) => {
  if (typeof document === 'undefined') return;
  let developerUnlocked = false;
  try {
    developerUnlocked = typeof window !== 'undefined' && window.sessionStorage?.getItem('developer-unlocked') === 'true';
  } catch {
    developerUnlocked = false;
  }

  // Only apply the preview-device clamp inside explicit preview layout contexts.
  // This prevents the entire app from being stuck at a mobile width on desktop.
  if (!developerUnlocked || !isPreviewLayoutContext()) {
    document.body.removeAttribute('data-preview-device');
    return;
  }

  if (value === 'auto') {
    document.body.removeAttribute('data-preview-device');
  } else {
    document.body.dataset.previewDevice = value;
  }
};

export const usePreviewDevice = () => {
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>(() => getStoredPreviewDevice());

  useEffect(() => {
    applyBodyPreviewDevice(previewDevice);
  }, [previewDevice]);

  useEffect(() => {
    let isActive = true;
    void fetchRemotePreviewDevice()
      .then((remote) => {
        if (!isActive || !remote) return;
        setPreviewDevice(remote);
        setStoredPreviewDevice(remote);
      })
      .catch(() => undefined);
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sync = () => setPreviewDevice(getStoredPreviewDevice());

    window.addEventListener('storage', sync);
    window.addEventListener(PREVIEW_DEVICE_EVENT, sync as EventListener);
    document.addEventListener('visibilitychange', sync);

    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(PREVIEW_DEVICE_EVENT, sync as EventListener);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const reapply = () => applyBodyPreviewDevice(previewDevice);
    window.addEventListener('developer-unlocked-changed', reapply as EventListener);
    window.addEventListener(PREVIEW_LAYOUT_CONTEXT_EVENT, reapply as EventListener);
    document.addEventListener('visibilitychange', reapply);

    return () => {
      window.removeEventListener('developer-unlocked-changed', reapply as EventListener);
      window.removeEventListener(PREVIEW_LAYOUT_CONTEXT_EVENT, reapply as EventListener);
      document.removeEventListener('visibilitychange', reapply);
    };
  }, [previewDevice]);

  const update = useCallback((next: PreviewDevice) => {
    setStoredPreviewDevice(next);
    setPreviewDevice(next);
  }, []);

  return { previewDevice, setPreviewDevice: update };
};

export const resolvePreviewOverrideBreakpoint = (previewDevice: PreviewDevice): 'mobile' | 'tablet' | 'desktop' | null => {
  // Only allow preview-device breakpoints to override responsive logic in explicit preview layout contexts.
  if (!isPreviewLayoutContext()) return null;
  if (previewDevice === 'auto') return null;
  
  // Map specific devices to breakpoint categories
  const mobileDevices: PreviewDevice[] = [
    'iphone-15-pro-max', 'iphone-15-pro', 'iphone-15',
    'iphone-14-pro-max', 'iphone-14-pro', 'iphone-14', 'iphone-se-3rd-gen',
    'pixel-8-pro', 'pixel-8', 'galaxy-s24-ultra', 'galaxy-s24', 'galaxy-z-fold5',
  ];
  const tabletDevices: PreviewDevice[] = [
    'ipad-pro-13', 'ipad-pro-11', 'ipad-air-5th-gen', 'ipad-mini-6th-gen',
    'galaxy-tab-s9-ultra', 'surface-pro-9',
  ];
  
  if (mobileDevices.includes(previewDevice)) return 'mobile';
  if (tabletDevices.includes(previewDevice)) return 'tablet';
  return 'desktop';
};

export const notifyPreviewLayoutContextChanged = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(PREVIEW_LAYOUT_CONTEXT_EVENT));
  } catch {
    // ignore
  }
};
